import { useEffect, useRef, useState } from 'react';
import { LogOut, Stars } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Sign-in control for the app header.
 *
 * Renders nothing at all when sign-in is unconfigured, so an incomplete Google
 * setup shows no dead button. Signing in is a convenience, never a gate — the
 * copy says "keep your bands", not "sign in to continue".
 */
export default function AuthButton() {
  const { enabled, user, signOut, renderButton, gsiReady } = useAuth();
  const [open, setOpen] = useState(false);
  const slotRef = useRef(null);

  // Render Google's own button as soon as the library is ready. Doing this
  // rather than calling prompt() matters: One Tap is throttled and blocked by
  // browsers and fails silently when it is, which just looks like nothing
  // happening. A rendered button always responds immediately.
  useEffect(() => {
    if (!enabled || user || !gsiReady) return;
    renderButton(slotRef.current);
  }, [enabled, user, gsiReady, renderButton]);

  if (!enabled) return null;

  if (!user) {
    return (
      <div className="shrink-0 flex items-center" data-testid="sign-in-slot">
        <div ref={slotRef} />
        {!gsiReady && (
          <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-slate-500 px-4 py-2">
            Loading&hellip;
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="relative shrink-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-full border border-white/10 hover:border-cyan-400/40
                   px-2 py-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {user.picture ? (
          <img src={user.picture} alt="" className="w-6 h-6 rounded-full" referrerPolicy="no-referrer" />
        ) : (
          <span className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-cyan-500" />
        )}
        <span className="hidden sm:inline font-mono text-[11px] tracking-wider text-slate-300 max-w-[140px] truncate">
          {user.name || user.email}
        </span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-56 rounded-xl border border-white/10 bg-[#0a0a0f]/95
                     backdrop-blur-xl p-3 z-50 shadow-xl"
        >
          <p className="text-xs text-slate-400 truncate mb-3">{user.email}</p>
          <Link
            to="/my-bands"
            onClick={() => setOpen(false)}
            className="w-full inline-flex items-center gap-2 text-xs text-slate-300 hover:text-white transition-colors mb-3"
          >
            <Stars className="w-3.5 h-3.5" />
            Your bands
          </Link>
          <button
            onClick={() => { setOpen(false); signOut(); }}
            className="w-full inline-flex items-center gap-2 text-xs text-slate-300 hover:text-white
                       transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
